import { type CfAccountInfo } from '@chainflip/rpc/types';
import { assert, unreachable } from '@chainflip/utils/assertion';
import { deferredPromise } from '@chainflip/utils/async';
import * as base58 from '@chainflip/utils/base58';
import { bytesToHex, hexToBytes } from '@chainflip/utils/bytes';
import {
  assetConstants,
  chainConstants,
  type ChainflipAsset,
  type ChainflipChain,
  addressTypes,
  chainflipAssets,
  type ChainMap,
} from '@chainflip/utils/chainflip';
import { POLKADOT_SS58_PREFIX } from '@chainflip/utils/consts';
import { isNotNullish, isNullish } from '@chainflip/utils/guard';
import * as ss58 from '@chainflip/utils/ss58';
import { capitalize, isHex, uncapitalize } from '@chainflip/utils/string';
import { MAX_TICK, MIN_TICK } from '@chainflip/utils/tickMath';
import { hexToTronAddress, tronAddressToHex } from '@chainflip/utils/tron';
import { type HexString } from '@chainflip/utils/types';
import { type ApiPromise } from '@polkadot/api';
import {
  type SubmittableResultValue,
  type SubmittableExtrinsic,
  type SubmittableResultSubscription,
} from '@polkadot/api/types';
import { type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { type ChainflipToken } from '@/shared/assets/tokens';
import {
  getAllBoostPoolBalancesForAccount,
  getBoostPoolCacheQuery,
} from '@/shared/graphql/cache/boost';
import {
  ACCOUNT_POOL_ORDERS,
  accountInfoRpcKey,
  getAccountPoolOrderQueryKey,
} from '@/shared/queryKeys';
import { chainLabel } from '@/shared/utils/chainflip';
import { chainflipAssetMap } from '@/shared/utils/env';
import { parseIntOrThrow } from '@/shared/utils/numbers';
import { type RpcOrder } from '@/shared/utils/orders';
import { humanReadableRole } from '@/shared/utils/rpc';
import { hexToUtf8 } from '@/shared/utils/strings';
import TokenAmount from '@/shared/utils/TokenAmount';

const addressEncoders: ChainMap<(address: string) => string> = {
  Assethub: (address) => ss58.toPublicKey(address),
  Solana: (address) => bytesToHex(base58.decode(address)),
  Tron: (address) => `0x${tronAddressToHex(address)}`,
  Arbitrum: (address) => address,
  Bitcoin: (address) => address,
  Ethereum: (address) => address,
  Bsc: (address) => address,
};

const addressDecodeMap: Record<
  ChainflipToken['chain']['chainflipId'],
  (addr: HexString) => string
> = {
  Bitcoin: hexToUtf8,
  Ethereum: (str) => str,
  Arbitrum: (str) => str,
  Bsc: (str) => str,
  Solana: (str) => base58.encode(hexToBytes(str)),
  Tron: (str) => hexToTronAddress(str),
  Assethub: (str) => ss58.encode({ data: str, ss58Format: POLKADOT_SS58_PREFIX }),
};

const transformEnum = <T, U>(values: readonly [T, ...T[]], cb: (value: T) => U): [U, ...U[]] =>
  values.map((v) => cb(v)) as [U, ...U[]];

const lowercaseAddressType = z
  .enum(transformEnum(addressTypes, uncapitalize))
  .transform((t) => capitalize(t));

const hexString = z.string().refine((str): str is `0x${string}` => /^0x[0-9a-f]*$/i.test(str));

const parsers = {
  'liquidityProvider.LiquidityDepositAddressReady': z
    .object({
      depositChainExpiryBlock: z.union([z.number(), hexString]).transform(parseIntOrThrow),
      depositAddress: z
        .record(lowercaseAddressType, z.string())
        .transform((obj) => Object.values(obj)[0])
        .refine(isHex),
      asset: z.enum(chainflipAssets).transform((asset) => chainflipAssetMap[asset]),
      channelId: z.number(),
      boostFee: z.number(),
    })
    .transform(({ asset, depositAddress, ...rest }) => {
      const address = addressDecodeMap[asset.chain.chainflipId](depositAddress);
      return { asset, depositAddress: address, ...rest };
    }),
  'tradingStrategy.StrategyDeployed': z.object({ strategyId: z.string() }),
} as const;

export type EventRecord = NonNullable<SubmittableResultValue['events']>[number];

export default abstract class ExtrinsicSDK {
  static createError(dispatchErr: SubmittableResultValue['dispatchError']): Error | null {
    if (!dispatchErr) return null;

    if (dispatchErr.isModule) {
      const { name, section, docs } = dispatchErr.registry.findMetaError(dispatchErr.asModule);

      const err = Error(`${section}.${name}:\n${docs.join(' ')}`, {
        cause: dispatchErr,
      });

      if (err.stack) err.stack = err.stack.split('\n').slice(0, 2).join('\n');

      return err;
    }

    return new Error(
      `The submitted extrinsic failed with an unexpected error: ${dispatchErr.toString()}`,
    );
  }

  static getEventArgs<P extends keyof typeof parsers>(
    name: P,
    records: EventRecord[],
  ): z.output<(typeof parsers)[P]> | null {
    const record = records.find((r) => `${r.event.section}.${r.event.method}` === name);

    if (!record) return null;

    const { data } = record.event;
    const { names } = data;

    if (!names) return null;

    const parser = parsers[name];

    return parser.parse(Object.fromEntries(names.map((n, index) => [n, data[index].toJSON()])));
  }

  protected accountIdHex = ss58.toPublicKey(this.accountId);

  constructor(
    protected account: CfAccountInfo,
    protected readonly apiPromise: ApiPromise,
    protected readonly accountId: `cF${string}`,
    protected readonly refetch: () => void,
    protected readonly queryClient: QueryClient,
  ) {}

  updateAccountInfo(account: CfAccountInfo) {
    this.account = account;
  }

  protected abstract submit(
    extrinsic: SubmittableExtrinsic<'promise'>,
    handleResult: Parameters<SubmittableExtrinsic<'promise'>['send']>[0],
  ): SubmittableResultSubscription<'promise'>;

  abstract signAndSendBatch(callback: (sdk: ExtrinsicSDK) => void): Promise<{
    hash: string;
    blockNumber: number;
    extrinsicIndexInBlock: number;
    events: EventRecord[];
  }>;

  protected async signAndSend(
    extrinsic: SubmittableExtrinsic<'promise'>,
    callbacks?: {
      onSubmitted?: () => void;
      onSettled?: () => void;
    },
  ) {
    const { resolve, reject, promise } = deferredPromise<{
      hash: string;
      blockNumber: number;
      extrinsicIndexInBlock: number;
      events: EventRecord[];
    }>();

    const handleResult: Parameters<SubmittableExtrinsic<'promise'>['send']>[0] = async ({
      status,
      dispatchError,
      txIndex,
      events,
    }) => {
      if (status.isReady) callbacks?.onSubmitted?.();

      const err = ExtrinsicSDK.createError(dispatchError);

      if (err) {
        return reject(err);
      }

      if (status.isInBlock) {
        const block = await this.apiPromise.derive.chain.getHeader(status.asInBlock);
        resolve({
          hash: status.asInBlock.toString(),
          blockNumber: block.number.toNumber(),
          extrinsicIndexInBlock: txIndex!,
          events,
        });
      }
    };

    const unsub = await this.submit(extrinsic, handleResult).catch(reject);

    return promise.finally(() => {
      callbacks?.onSettled?.();
      unsub?.();
      this.refetch();
    });
  }

  private ensureRoleIsOneOf<const T extends CfAccountInfo['role'][]>(
    ...roles: T
  ): Extract<CfAccountInfo, { role: T[number] }> {
    if (!(roles as CfAccountInfo['role'][]).includes(this.account.role)) {
      throw new Error(
        `Current account is not ${roles.map((role) => humanReadableRole[role]).join(' or ')}`,
      );
    }

    return this.account as Extract<CfAccountInfo, { role: T[number] }>;
  }

  private ensureRole<T extends CfAccountInfo['role']>(
    role: T,
  ): Extract<CfAccountInfo, { role: T }> {
    return this.ensureRoleIsOneOf(role);
  }

  private ensureRefundAddress(asset: ChainflipAsset, action: string) {
    const account = this.ensureRole('liquidity_provider');
    const { chain } = assetConstants[asset];
    const address = account.refund_addresses[chain];
    if (!address) {
      throw new Error(`A refund address must be set for ${chainLabel(chain)} before ${action}`);
    }
    return address;
  }

  async setLpRole() {
    this.ensureRole('unregistered');
    return this.signAndSend(this.apiPromise.tx.liquidityProvider.registerLpAccount());
  }

  async registerAsOperator(
    feeBps: number,
    delegationAcceptance: 'Allow' | 'Deny',
    vanityName?: string,
  ) {
    this.ensureRole('unregistered');

    return this.signAndSend(
      this.apiPromise.tx.validator.registerAsOperator({ feeBps, delegationAcceptance }, vanityName),
      {
        onSettled: () => {
          this.queryClient.invalidateQueries({
            queryKey: ['cf_accounts'],
          });
          this.queryClient.invalidateQueries({
            queryKey: accountInfoRpcKey(this.accountId),
          });
        },
      },
    );
  }

  async accountRoles_setVanityName(vanityName: string) {
    return this.signAndSend(this.apiPromise.tx.accountRoles.setVanityName(vanityName), {
      onSettled: () => {
        this.queryClient.invalidateQueries({
          queryKey: ['cf_accounts'],
        });
        this.queryClient.invalidateQueries({
          queryKey: accountInfoRpcKey(this.accountId),
        });
      },
    });
  }

  async deregisterAccount() {
    switch (this.account.role) {
      case 'unregistered':
        throw new Error('Account is already unregistered');
      case 'liquidity_provider':
        return this.signAndSend(this.apiPromise.tx.liquidityProvider.deregisterLpAccount());
      case 'broker':
      case 'validator':
      default:
        throw new Error(`Cannot deregister ${humanReadableRole[this.account.role]} account`);
    }
  }

  async setRefundAddress(chain: ChainflipChain, address: string) {
    this.ensureRole('liquidity_provider');
    const { addressType } = chainConstants[chain];
    return this.signAndSend(
      this.apiPromise.tx.liquidityProvider.registerLiquidityRefundAddress({
        [addressType]: addressEncoders[chain](address),
      }),
    );
  }

  async openChannel(asset: ChainflipAsset, boostFee: number, onSubmitted: () => void) {
    this.ensureRefundAddress(asset, 'depositing liquidity');
    const { events, blockNumber } = await this.signAndSend(
      this.apiPromise.tx.liquidityProvider.requestLiquidityDepositAddress(asset, boostFee || 0),
      { onSubmitted },
    );
    const result = ExtrinsicSDK.getEventArgs(
      'liquidityProvider.LiquidityDepositAddressReady',
      events,
    );
    assert(result, 'failed to parse information from event');
    return { ...result, blockNumber };
  }

  async withdrawLiquidity(asset: ChainflipAsset, amount: TokenAmount, address?: string) {
    const refundAddress = this.ensureRefundAddress(asset, 'withdrawing liquidity');
    const { chain } = assetConstants[asset];
    const { addressType } = chainConstants[chain];

    return this.signAndSend(
      this.apiPromise.tx.liquidityProvider.withdrawAsset(amount.toString(), asset, {
        [addressType]: addressEncoders[chain](address ?? refundAddress),
      }),
    );
  }

  async redeemFlip(amount: 'Max' | { Exact: string }, ethAddress: string) {
    assert(
      isNullish(this.account.bound_redeem_address) ||
        ethAddress === this.account.bound_redeem_address,
      "The provided address does not match this account's bound redeem address.",
    );

    const boundExecutorAddress = z
      .union([z.null(), hexString])
      .parse(
        (await this.apiPromise.query.funding.boundExecutorAddress(this.accountIdHex)).toJSON(),
      );

    return this.signAndSend(
      this.apiPromise.tx.funding.redeem(amount, ethAddress, boundExecutorAddress),
    );
  }

  async setLimitOrder(params: {
    baseAsset: ChainflipAsset;
    quoteAsset: ChainflipAsset;
    side: 'Buy' | 'Sell';
    orderId: bigint;
    tick: number;
    sellAmount: TokenAmount;
    dispatchAt?: bigint | null;
    closeOrderAt?: bigint | null;
  }) {
    this.ensureRefundAddress(params.baseAsset, 'placing this limit order');
    this.ensureRefundAddress(params.quoteAsset, 'placing this limit order');

    return this.signAndSend(
      this.apiPromise.tx.liquidityPools.setLimitOrder(
        params.baseAsset,
        params.quoteAsset,
        params.side,
        params.orderId.toString(),
        params.tick.toString(),
        params.sellAmount.toString(),
        isNotNullish(params.dispatchAt) ? params.dispatchAt.toString() : null,
        isNotNullish(params.closeOrderAt) ? params.closeOrderAt.toString() : null,
      ),
      {
        onSettled: () => {
          this.queryClient.invalidateQueries({
            queryKey: getAccountPoolOrderQueryKey(
              params.baseAsset,
              params.quoteAsset,
              this.accountId,
            ),
          });
        },
      },
    );
  }

  async setRangeOrder(params: {
    baseAsset: ChainflipAsset;
    quoteAsset: ChainflipAsset;
    orderId: bigint;
    lowerTick: number;
    upperTick: number;
    maxBaseAmount: TokenAmount;
    maxQuoteAmount: TokenAmount;
  }) {
    this.ensureRefundAddress(params.baseAsset, 'placing this range order');
    this.ensureRefundAddress(params.quoteAsset, 'placing this range order');

    return this.signAndSend(
      this.apiPromise.tx.liquidityPools.setRangeOrder(
        params.baseAsset,
        params.quoteAsset,
        params.orderId.toString(),
        [params.lowerTick.toString(), params.upperTick.toString()],
        {
          AssetAmounts: {
            maximum: {
              base: params.maxBaseAmount.toString(),
              quote: params.maxQuoteAmount.toString(),
            },
          },
        },
      ),
      {
        onSettled: () => {
          this.queryClient.invalidateQueries({
            queryKey: getAccountPoolOrderQueryKey(
              params.baseAsset,
              params.quoteAsset,
              this.accountId,
            ),
          });
        },
      },
    );
  }

  async setBrokerRole() {
    this.ensureRole('unregistered');

    return this.signAndSend(this.apiPromise.tx.swapping.registerAsBroker());
  }

  async addBoostFunds(asset: ChainflipAsset, amount: TokenAmount, poolTier: number) {
    this.ensureRole('liquidity_provider');

    return this.signAndSend(
      this.apiPromise.tx.lendingPools.addBoostFunds(asset, amount.toString(), poolTier),
      {
        onSettled: () => {
          setTimeout(() => {
            this.queryClient.invalidateQueries({
              queryKey: [getAllBoostPoolBalancesForAccount],
            });
            this.queryClient.invalidateQueries({
              queryKey: [getBoostPoolCacheQuery],
            });
          }, 2000);
        },
      },
    );
  }

  async stopBoosting(asset: ChainflipAsset, poolTier: number) {
    this.ensureRole('liquidity_provider');

    return this.signAndSend(this.apiPromise.tx.lendingPools.stopBoosting(asset, poolTier), {
      onSettled: () => {
        setTimeout(() => {
          this.queryClient.invalidateQueries({
            queryKey: [getAllBoostPoolBalancesForAccount],
          });
          this.queryClient.invalidateQueries({
            queryKey: [getBoostPoolCacheQuery],
          });
        }, 2000);
      },
    });
  }

  async cancelOrdersBatch(orders: RpcOrder[]) {
    this.ensureRole('liquidity_provider');

    const formattedOrders = orders.map((order) =>
      order.type === 'range'
        ? {
            Range: {
              id: order.id,
              baseAsset: order.asset,
              quoteAsset: 'Usdc',
            },
          }
        : {
            Limit: {
              id: order.id,
              baseAsset: order.asset,
              quoteAsset: 'Usdc',
              side: order.type === 'bid' ? 'Buy' : 'Sell',
            },
          },
    );

    return this.signAndSend(this.apiPromise.tx.liquidityPools.cancelOrdersBatch(formattedOrders), {
      onSettled: () => {
        this.queryClient.invalidateQueries({
          queryKey: [ACCOUNT_POOL_ORDERS],
        });
      },
    });
  }

  async scheduleSwap(
    amount: string,
    inputAsset: ChainflipAsset,
    outputAsset: ChainflipAsset,
    retryDuration: number,
    priceLimits: {
      min_price: string;
      max_oracle_price_slippage?: number;
    },
    dcaParams?: {
      /// The number of individual swaps to be executed
      number_of_chunks: number;
      /// The interval in blocks between each swap.
      chunk_interval: number;
    },
  ) {
    this.ensureRefundAddress(inputAsset, 'initiating this swap');
    this.ensureRefundAddress(outputAsset, 'initiating this swap');

    return this.signAndSend(
      this.apiPromise.tx.liquidityProvider.scheduleSwap(
        amount,
        inputAsset,
        outputAsset,
        retryDuration,
        priceLimits,
        dcaParams,
      ),
    );
  }

  async deployStrategy(
    strategy:
      | {
          type: 'INVENTORY_BASED';
          minSellTick: number;
          maxSellTick: number;
          minBuyTick: number;
          maxBuyTick: number;
          baseAsset: ChainflipAsset;
        }
      | {
          type: 'ORACLE_TRACKING';
          minSellOffsetTick: number;
          maxSellOffsetTick: number;
          minBuyOffsetTick: number;
          maxBuyOffsetTick: number;
          baseAsset: ChainflipAsset;
          quoteAsset: ChainflipAsset;
        },
    funding: [ChainflipAsset, string][],
  ) {
    this.ensureRole('liquidity_provider');

    const { type, ...params } = strategy;
    const strategyVariant =
      type === 'INVENTORY_BASED' ? { InventoryBased: params } : { OracleTracking: params };

    const result = await this.signAndSend(
      this.apiPromise.tx.tradingStrategy.deployStrategy(
        strategyVariant,
        new Map(
          funding
            .toSorted(([a], [b]) => a.localeCompare(b))
            .map(([asset, amount]) => [{ [asset]: {} }, amount]),
        ),
      ),
      {
        onSettled: () => {
          this.queryClient.invalidateQueries({
            queryKey: ['cf_get_trading_strategies'],
          });
        },
      },
    );
    const strategyData = ExtrinsicSDK.getEventArgs(
      'tradingStrategy.StrategyDeployed',
      result.events,
    );
    assert(strategyData, 'failed to parse information from event');

    return { ...result, strategyData };
  }

  async closeStrategy(strategyId: string) {
    this.ensureRole('liquidity_provider');

    return this.signAndSend(this.apiPromise.tx.tradingStrategy.closeStrategy(strategyId), {
      onSettled: () => {
        this.queryClient.invalidateQueries({
          queryKey: ['cf_get_trading_strategies'],
        });
      },
    });
  }

  async addFundsToStrategy(strategyId: string, funding: [ChainflipAsset, string][]) {
    this.ensureRole('liquidity_provider');

    try {
      return await this.signAndSend(
        this.apiPromise.tx.tradingStrategy.addFundsToStrategy(
          strategyId,
          new Map(
            funding
              .toSorted(([a], [b]) => a.localeCompare(b))
              .map(([asset, amount]) => [{ [asset]: {} }, amount]),
          ),
        ),
      );
    } finally {
      this.queryClient.invalidateQueries({
        queryKey: ['cf_get_trading_strategies'],
      });
    }
  }

  async addLenderFunds(asset: ChainflipAsset, amount: string) {
    this.ensureRole('liquidity_provider');

    return this.signAndSend(this.apiPromise.tx.lendingPools.addLenderFunds(asset, amount));
  }

  async removeLenderFunds(asset: ChainflipAsset, amount?: string) {
    this.ensureRole('liquidity_provider');

    return this.signAndSend(this.apiPromise.tx.lendingPools.removeLenderFunds(asset, amount));
  }

  async requestLoan(loanAsset: ChainflipAsset, loanAmount: string, brokerAccount?: `0x${string}`) {
    this.ensureRole('liquidity_provider');
    return this.signAndSend(
      this.apiPromise.tx.lendingPools.requestLoan(loanAsset, loanAmount, brokerAccount),
    );
  }

  async expandLoan(loanId: bigint, extraAmountToBorrow: string) {
    this.ensureRole('liquidity_provider');

    return this.signAndSend(
      this.apiPromise.tx.lendingPools.expandLoan(loanId.toString(), extraAmountToBorrow),
    );
  }

  async makeRepayment(loanId: bigint, amount: 'Full' | { Exact: string }) {
    this.ensureRole('liquidity_provider');

    return this.signAndSend(
      this.apiPromise.tx.lendingPools.makeRepayment(loanId.toString(), amount),
    );
  }

  async initiateVoluntaryLiquidation() {
    this.ensureRole('liquidity_provider');

    return this.signAndSend(this.apiPromise.tx.lendingPools.initiateVoluntaryLiquidation());
  }

  async stopVoluntaryLiquidation() {
    this.ensureRole('liquidity_provider');

    return this.signAndSend(this.apiPromise.tx.lendingPools.stopVoluntaryLiquidation());
  }

  private async getPaymentInfo(extrinsic: SubmittableExtrinsic<'promise'>) {
    const paymentInfo = await extrinsic.paymentInfo(this.accountIdHex);
    return TokenAmount.fromAsset(paymentInfo.partialFee.toString(), 'Flip');
  }

  async estimateExtrinsicFee(method: 'setLimitOrder' | 'setRangeOrder') {
    switch (method) {
      case 'setLimitOrder': {
        return this.getPaymentInfo(
          this.apiPromise.tx.liquidityPools.setLimitOrder(
            'Flip',
            'Usdc',
            'Buy',
            BigInt(Date.now()),
            0,
            String(1e18),
            null,
            null,
          ),
        );
      }
      case 'setRangeOrder': {
        return this.getPaymentInfo(
          this.apiPromise.tx.liquidityPools.setRangeOrder(
            'Flip',
            'Usdc',
            BigInt(Date.now()),
            [MIN_TICK.toString(), MAX_TICK.toString()],
            {
              AssetAmounts: {
                maximum: {
                  base: String(1e18),
                  quote: String(1e6),
                },
              },
            },
          ),
        );
      }
      default:
        return unreachable(method);
    }
  }

  async operator_allowDelegator(accountId: `cF${string}`) {
    this.ensureRole('operator');
    return this.signAndSend(this.apiPromise.tx.validator.allowDelegator(accountId));
  }

  async operator_blockDelegator(accountId: `cF${string}`) {
    this.ensureRole('operator');
    return this.signAndSend(this.apiPromise.tx.validator.blockDelegator(accountId));
  }

  async operator_claimValidator(validatorId: `cF${string}`) {
    this.ensureRole('operator');
    return this.signAndSend(this.apiPromise.tx.validator.claimValidator(validatorId));
  }

  async operator_removeValidator(validatorId: `cF${string}`) {
    this.ensureRole('operator');
    return this.signAndSend(this.apiPromise.tx.validator.removeValidator(validatorId));
  }

  async operator_updateOperatorSettings(feeBps: number, delegationAcceptance: 'Allow' | 'Deny') {
    this.ensureRole('operator');
    return this.signAndSend(
      this.apiPromise.tx.validator.updateOperatorSettings({ feeBps, delegationAcceptance }),
    );
  }

  async operator_deregisterAsOperator() {
    this.ensureRole('operator');
    return this.signAndSend(this.apiPromise.tx.validator.deregisterAsOperator());
  }

  async delegate(operator: `cF${string}`, amount: TokenAmount | 'Max') {
    this.ensureRoleIsOneOf('liquidity_provider', 'unregistered');

    return this.signAndSend(
      this.apiPromise.tx.validator.delegate(
        operator,
        amount === 'Max' ? { Max: null } : { Some: amount.toBigInt().toString() },
      ),
    );
  }

  async transferFlipToOnChainBalance(amount: TokenAmount) {
    this.ensureRole('liquidity_provider');

    return this.signAndSend(
      this.apiPromise.tx.liquidityProvider.transferFlipToOnChainBalance(
        amount.toBigInt().toString(),
      ),
    );
  }

  async undelegate(amount: TokenAmount | 'Max') {
    this.ensureRoleIsOneOf('liquidity_provider', 'unregistered');

    return this.signAndSend(
      this.apiPromise.tx.validator.undelegate(
        amount === 'Max' ? { Max: null } : { Some: amount.toBigInt().toString() },
      ),
    );
  }

  abstract signAccountCreationPayload(
    asset: ChainflipAsset,
    refundAddress: string,
  ): Promise<{
    signatureData: {
      Ethereum: { signature: `0x${string}`; signer: `0x${string}`; sigType: 'Eip712' };
    };
    transactionMetadata: { nonce: number; expiryBlock: number };
    asset: ChainflipAsset;
    refundAddress: string;
  }>;
}
